import { Subject } from "rxjs";
import { ModuleExportFormat } from "src/AngularJS/Dashboard/Models/module-export-format.model";
import ModuleDataGroupItem from "src/shared/models/module-data-group-item";
import { CopyModule, DashboardModuleInstance } from "../models/module-info.model";
import { ModuleManagerGenericSettingsControls, ModuleManagerModuleSettings, ModuleSettingsView, WorkspaceApiModel } from "../models/module-manager/module-manager-settings.models";
import { ClearViewUtils } from "../utils";
import { AgContextMenuServiceBase, GroupContextMenuParams } from "./ag-context-menu-service-base";
import { CvApiServiceBase } from "./cv-api-service-base";
import { CvConfigurationServiceBase } from "./cv-configuration-service-base";
import { CvLocalizationServiceBase } from "./cv-localization-service-base";
import { DashboardServiceBase } from "./dashboard-service-base";

export class ModuleContainerServiceBase {
    moduleKeyBase = 'b7e436e0-291d-4687-a672-f4cd97';
    /** should only ever be 0 or 1 but may increase because multiple module containers will be changing this */
    modulesDragging = 0;
    focusedModule: string = null;
    menuLocalizationKeys: Map<string, string> = null;
    menuLocalizationPromise: PromiseLike<Map<string, string>> = null;
    nameLocalizationKeys: Map<string, string> = null;
    nameLocalizationPromise: PromiseLike<Map<string, string>> = null;
    resizeWaitTimeout: number = null;

    exportFormats: { [name: string]: ModuleExportFormat } = {};
    moduleGainedFocus = new Subject<string>();
    moduleLostFocus = new Subject<string>();
    closeAllMenusEvent = new Subject<void>();
    addModuleToDashboard = new Subject<DashboardModuleInstance>();

    private _currentSettingsView = ModuleSettingsView.Slideout;
    get currentSettingsView() { return this._currentSettingsView; }
    set currentSettingsView(v: ModuleSettingsView) {
        this._currentSettingsView = v;
        this.saveSettingState();
    }

    constructor(
        fromSharedService: boolean = false,
        private cvApiService?: CvApiServiceBase,
        private cvLocalizationService?: CvLocalizationServiceBase,
        private dashboardService?: DashboardServiceBase,
        private agContextMenuService?: AgContextMenuServiceBase,
        private cvConfigurationService?: CvConfigurationServiceBase,
    ) {
        if (fromSharedService)
            return ModuleContainerServiceBase.getServiceBase();

        if (!cvApiService)
            this.cvApiService = CvApiServiceBase.getServiceBase();
        if (!cvLocalizationService)
            this.cvLocalizationService = CvLocalizationServiceBase.getServiceBase();
        if (!dashboardService)
            this.dashboardService = DashboardServiceBase.getServiceBase();
        if (!agContextMenuService)
            this.agContextMenuService = AgContextMenuServiceBase.getServiceBase();
        if (!cvConfigurationService)
            this.cvConfigurationService = CvConfigurationServiceBase.getServiceBase();

        this.loadNameLocalization();
        this.loadMenuLocalization();

        // wait for workspace to be ready
        let workspace: WorkspaceApiModel = null;
        const isSharedURL = window.location.pathname.includes('SharedDashboard.aspx');
        let sharedKey = '';
        if (isSharedURL) {
            const urlOptions: Map<string, string> = new Map<string, string>();

            if (window.location.search) {
                // break up url
                const queriedOptions = window.location.search.split('&');
                // create kv pairs and add to urlOptions map
                queriedOptions.forEach(option => {
                    const kvPair = option.split('=');
                    urlOptions.set(kvPair[0].replace('?',''), kvPair[1]);
                });
            }
            // set sharedKey from urlOptions map
            if (urlOptions.has('id')) {
                sharedKey = urlOptions.get('id');
            }
        }
        this.dashboardService.getUserWorkspace(isSharedURL, sharedKey)?.then(ws => workspace = ws).finally(() => { //skip this if we are unit testing
            if (this.cvConfigurationService.isCxOne) // CXOne always uses their modal
                this._currentSettingsView = ModuleSettingsView.CXOneModal;
            else // set the current view to the new view
                this._currentSettingsView = ModuleSettingsView.Slideout;
        });

        this.agContextMenuService.intervalReportClicked.subscribe(
            ({moduleKey, groupData, moduleTypeId, isParentAgGridModule}: GroupContextMenuParams) =>
                this.openIntervalFromModule(moduleKey, groupData, moduleTypeId, isParentAgGridModule)
        );
        this.agContextMenuService.summaryReportClicked.subscribe(
            ({moduleKey, groupData,moduleTypeId, isParentAgGridModule}: GroupContextMenuParams) =>
                this.openSummaryFromModule(moduleKey, groupData, moduleTypeId, isParentAgGridModule)
        );

        this.exportFormats = {
            csv: {
                description: 'CSV',
                mime: 'text/csv',
                extension: 'csv',
            },
            excel: {
                description: 'Excel',
                mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                extension: 'xlsx',
            },
            excelNoObjectives: {
                description: 'Excel',
                mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                extension: 'xlsx',
            },
        };

        this.agContextMenuService.singleRowExportClicked.subscribe(
            ({moduleKey, groupData, moduleTypeId, format, moduleName}: GroupContextMenuParams) =>
                this.singleRowExport(moduleKey, groupData, moduleTypeId, moduleName, format)
        );
    }

    static getServiceBase(): ModuleContainerServiceBase {
        return ClearViewUtils.getSharedService(ModuleContainerServiceBase, 'ModuleContainerServiceBase');
    }

    /** Returns a module key that will be the same for all modules with the specified id */
    getGlobalKey(moduleId: number) {
        return this.moduleKeyBase + String(moduleId).padStart(4, "0");
    }

    setFocused(moduleKey: string) {
        const lastFocused = this.focusedModule;
        if (lastFocused !== moduleKey) { // only update if focus changed
            if (lastFocused != null) {
                // if there was a previously focused module, tell it the focus was lost
                this.moduleLostFocus.next(lastFocused);
            }

            this.focusedModule = moduleKey;
            this.moduleGainedFocus.next(moduleKey);
        }
    }

    async copyModule(moduleKey: string, moduleID: number, dashboardKey: string) {
        try {
            const url = `/api/v2/copyModule/${moduleKey}/${moduleID}/dashboard/${dashboardKey}`;
            const response = await this.cvApiService.get<CopyModule>(url);
            if (response.status_code < 200 || response.status_code >= 300)
                throw response;

            return response;
        } catch (e) { /* error is shown through toast */ }
    }

    async openIntervalFromModule(moduleKey: string, groupInfo: ModuleDataGroupItem, moduleTypeId?: number,
        isParentAgGridModule: boolean = true) {
        try {
            const url = '/api/v2/modules/spawnintervalmodulefromexistingmodule';
            const response = await this.cvApiService.post<DashboardModuleInstance>(url, {
                ModuleKey: moduleKey,
                GroupInfo: groupInfo,
                ModuleTypeId: moduleTypeId,
                IsParentAgGridModule: isParentAgGridModule,
            });

            if (response.status_code < 200 || response.status_code >= 300)
                throw response;

            this.addModuleToDashboard.next(response.data);
            return response;
        } catch { /* error is shown through toast */ }
    }

    async openSummaryFromModule(moduleKey: string, groupInfo: ModuleDataGroupItem, moduleTypeId?: number,
        isParentAgGridModule: boolean = true) {
        try {
            const url = '/api/v2/modules/spawnsummarymodulefromexistingmodule';
            const response = await this.cvApiService.post<DashboardModuleInstance>(url, {
                ModuleKey: moduleKey,
                GroupInfo: groupInfo,
                ModuleTypeId: moduleTypeId,
                IsParentAgGridModule: isParentAgGridModule,
            });

            if (response.status_code < 200 || response.status_code >= 300)
                throw response;

            this.addModuleToDashboard.next(response.data);
            return response;
        } catch { /* error is shown through toast */ }
    }

    async singleRowExport(
        moduleKey: string,
        groupInfo: ModuleDataGroupItem,
        moduleId: number,
        moduleName: string,
        format: string = 'excel',
        isCXOne: boolean = false
    ) {
        const queryParams = new URLSearchParams({
            format,
            moduleName,
            isCXOne: isCXOne.toString(),
        });
        const url = `/api/v2/modules/singleRowExport/module/${moduleKey}?${queryParams.toString()}`;

        const requestData = {
            ModuleKey: moduleKey,
            GroupInfo: groupInfo,
            ModuleTypeId: moduleId,
        };

        const response = await this.cvApiService.post<string>(url, requestData);
        if (response.status_code < 200 || response.status_code >= 300) {
            throw response;
        }

        const formatDetails = this.exportFormats[format];
        const timestamp = new  Date().toString('yyyymmddhhmmss');
        const fileName = `${moduleName} Single Row Export_${timestamp}`;

        const fullFileName = `${fileName}.${formatDetails.extension}`;
        this.downloadFile(response.data, formatDetails.mime, fullFileName);
    }

    async loadModuleState(moduleKey: string, stateKey: string) {
        return await this.cvApiService.get<string>(`/api/v2/loadModuleState/module/${moduleKey}/state/${stateKey}`);
    }

    async loadModuleStates(moduleKey: string, isSharedURL = false, sharedKey = '') {
        const result = await this.cvApiService.get<Record<string, any>>(`/api/v2/modules/${moduleKey}/state?isSharedURL=${isSharedURL}&sharedKey=${sharedKey}`);
        return result.data;
    }

    /** DEPRECATED only used for subscription preview until it gets updated to use the new module container */
    async saveModuleState(moduleKey: string, stateKey: string, stateData: string) {
        const url = `/api/v2/saveModuleState/module/${moduleKey}/state/${stateKey}`;
        return await this.cvApiService.post<string>(url, JSON.stringify(stateData));
    }

    async saveModuleStates(moduleKey: string, stateData: Record<string, any>) {
        return this.cvApiService.post(`/api/v2/modules/${moduleKey}/state`, JSON.stringify(stateData));
    }

    async loadControlSettings(moduleID: number, moduleKey: string) {
        return await this.cvApiService.get<ModuleManagerModuleSettings>(`/api/v2/module/${moduleID}/control-settings/${moduleKey}`);
    }

    async loadGenericSettings(moduleID: number, moduleKey: string) {
        const url = `/api/v2/genericSettings/module/${moduleID}/key/${moduleKey}`;
        return await this.cvApiService.get<ModuleManagerGenericSettingsControls>(url);
    }

    async cancelGenericSettings(moduleID: number, moduleKey: string) {
        return await this.cvApiService.delete(`/api/v2/tempGenericSettings/module/${moduleID}/key/${moduleKey}`);
    }

    async applyGenericSettings(moduleID: number, moduleKey: string) {
        return await this.cvApiService.put<ModuleManagerModuleSettings>(`/api/v2/genericSettings/module/${moduleID}/key/${moduleKey}`);
    }

    async saveSettingState() {
        const useNewSettings = this.currentSettingsView === ModuleSettingsView.Slideout;
        this.cvApiService.post(`/api/v2/workspace/${this.dashboardService.workspaceKey}/use-new-settings`, useNewSettings);
    }

    async loadNameLocalization() {
        this.nameLocalizationPromise = this.cvLocalizationService.GetSection("Module_Names");
        this.nameLocalizationKeys = await this.nameLocalizationPromise;
        return this.nameLocalizationKeys;
    }

    async loadMenuLocalization() {
        this.menuLocalizationPromise = this.cvLocalizationService.GetSection("Module_Menu");
        this.menuLocalizationKeys = await this.menuLocalizationPromise;
        return this.menuLocalizationKeys;
    }

    /**
     * Downloads a file from a byte array string
     *
     * @param data The byte[] string returned from the endpoint
     * @param mime The mime type to mark the file as
     * @param fileName The name of the file to be downloaded
     * @memberof ModuleContainerComponent
     */
    downloadFile(data: string, mime: string, fileName: string) {
        const decodedData = atob(data);
        const buffer = new ArrayBuffer(decodedData.length);
        const view = new Uint8Array(buffer);
        for (let i = 0; i !== decodedData.length; ++i) {
            // eslint-disable-next-line no-bitwise
            view[i] = decodedData.charCodeAt(i) & 0xFF;
        }

        const fileData = [buffer];
        const blobObject = new Blob(fileData, { type: mime });

        // create fake link
        const linkElement = document.createElement('a');
        document.body.appendChild(linkElement);
        linkElement.setAttribute('style', 'display: none;');
        const url = URL.createObjectURL(blobObject);
        linkElement.href =  url;
        linkElement.download = fileName;

        // start download
        linkElement.click();

        // cleanup
        URL.revokeObjectURL(linkElement.href);
        linkElement.remove();
    };
}
